/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';
import React, {forwardRef, HTMLAttributes, InputHTMLAttributes, ReactNode} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import { faCheck} from '@fortawesome/free-solid-svg-icons';
import ReactSwitch from 'react-switch';
import ReactDatePicker, {ReactDatePickerProps} from 'react-datepicker';
import {hexToRgba} from 'utils';
import Button from './Button';
import { InputNumber } from '@mayo/mayo-ui-gakki';
import {isNilOrEmpty} from '../app/gakki/utils/validate';
import InfoTip from '../app/gakki/components/info-tip/info-tip';

export const FormItemGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
`;

type FormItemType = {
  itemName: string;
  label: string;
  width?: string;
  required?: boolean;
  children?: ReactNode;
  info?: string | ReactNode;
  labelProps?: {[key: string]: any};
  css?: any;
  hidden?: boolean;
};
export function FormItem({
  itemName,
  label,
  required = false,
  labelProps,
  children,
  info,
  hidden,
  width,
  ...rest
}: FormItemType) {
  return (
    <div
      css={{
        display: hidden ? 'none' : 'flex',
        flexDirection: 'column',
        marginBottom: 10,
        width: width || 'auto',
        position: 'relative',
      }}
      {...rest}
    >
      <label htmlFor={itemName} {...labelProps}>
        {label}
        {info && <InfoTip info={info} />}{' '}
        {required ? <RequireMarker /> : undefined}
      </label>
      {children}
    </div>
  );
}

export const Input = styled.input`
  box-sizing: content-box;
  height: 35px;
  border-radius: 2px;
  border: 1px solid
    ${(props: {error?: string | boolean}) =>
      props.error ? '#ff4d4f' : '#d5dadd'};
  outline: none;
  padding: 0 13px;
  background-color: #f9f9f9;

  &:disabled {
    opacity: 0.5;
  }
  &:focus {
    border-color: transparent;
    box-shadow: 0 0 0 2px #93dcff;
  }
`;

export const ErrorMessage = styled.p`
  margin: 0;
  color: #ff3b30;
`;

type FormGroupProps = {
  columnTemplates?: string | number;
};
export const FormGroup = styled.div<FormGroupProps>`
  display: grid;
  grid-template-columns: ${props =>
    props.columnTemplates
      ? typeof props.columnTemplates === 'number'
        ? `repeat(${props.columnTemplates}, 1fr)`
        : props.columnTemplates
      : '1fr 1fr auto'};
  grid-column-gap: 15px;
  width: 100%;
  margin-bottom: 10px;
  '&:last-child': {
    margin-bottom: 0;
  }
`;
export const RequireMarker = () => (
  <span css={{color: 'red', marginLeft: 5}}>*</span>
);

export function Checkbox({
  labelName,
  error,
  margin = '0',
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  labelName?: string;
  error?: boolean;
  margin?: string;
}) {
  return (
    <label css={{display: 'flex', alignItems: 'center', margin}}>
      <input
        type={'checkbox'}
        css={{marginRight: 5, outline: error ? '1px solid red' : 'none'}}
        {...rest}
      />
      {labelName}
    </label>
  );
}

export function BooleanCheckbox({
  error,
  value,
  onChange,
  ...rest
}: Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'onChange' | 'value'
> & {
  error?: boolean;
  value?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <label css={{display: 'flex', alignItems: 'center', margin: 0}}>
      <input
        type={'checkbox'}
        css={{
          marginRight: 5,
          outline: error ? '1px solid red' : 'none',
          display: 'none',
        }}
        {...rest}
        checked={!!value}
      />
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: '273px 273px',
          columnGap: 15,
        }}
      >
        <Button
          aria-label="yes"
          css={{fontSize: 13}}
          variant={value ? 'primary' : 'default'}
          onClick={onChange ? () => onChange(true) : undefined}
        >
          <div css={{display: 'flex', justifyContent: 'space-between'}}>
            是{value && <FontAwesomeIcon icon={faCheck} />}
          </div>
        </Button>
        <Button
          aria-label="no"
          css={{fontSize: 13}}
          variant={value ? 'default' : 'primary'}
          onClick={onChange ? () => onChange(false) : undefined}
        >
          <div css={{display: 'flex', justifyContent: 'space-between'}}>
            否{!value && <FontAwesomeIcon icon={faCheck} />}
          </div>
        </Button>
      </div>
    </label>
  );
}

export function RadioButton({
  labelName,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  labelName: string;
}) {
  return (
    <label
      css={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 13px',
        height: 35,
        borderRadius: 2,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: props.checked ? '#26abe3' : '#d5dadd',
        cursor: 'pointer',
        margin: 0,
        '&:hover': {
          borderColor: '#26abe3',
        },
      }}
    >
      {labelName}
      {props.checked ? (
        <FontAwesomeIcon icon={faCheck} css={{color: '#26abe3'}} />
      ) : undefined}
      <input
        type={'radio'}
        {...props}
        css={{opacity: '0', position: 'absolute'}}
      />
    </label>
  );
}

export function Radio({
  children,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  children: string;
}) {
  return (
    <label
      css={{
        margin: 0,
      }}
    >
      <input type={'radio'} {...props} css={{marginRight: '10px'}} />
      {children}
    </label>
  );
}
export const HorizontalGroup = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
`;

type TimeRange = {
  max: number;
  min: number;
  step: number;
};

type TimeInput = {
  onChange: (time: string) => void;
  value: string;
  hourOptions?: TimeRange;
  minuteOptions?: TimeRange;
  error?: string | boolean | undefined;
  disabled?: boolean;
  autoComplete?: boolean;
};

export function TimesInput({
  value,
  onChange,
  hourOptions = {
    min: 0,
    max: 23,
    step: 1,
  },
  minuteOptions = {
    min: 0,
    max: 59,
    step: 1,
  },
  error,
  disabled = false,
  autoComplete = false,
}: TimeInput) {
  const hourMaxLength = hourOptions.max.toString().length;
  const [times, setTimes] = React.useState<{
    hour: number | null;
    minute: number | null;
  }>(() => {
    const nullState = {
      hour: null,
      minute: null,
    };
    if (isNilOrEmpty(value)) {
      return nullState;
    }

    const times = value.split(':');
    if (times.length < 2) {
      return nullState;
    }

    if (
      times.length !== 2 ||
      isNaN(parseInt(times[0])) ||
      isNaN(parseInt(times[1]))
    ) {
      return nullState;
    }

    return {
      hour: parseInt(times[0]),
      minute: parseInt(times[1]),
    };
  });
  React.useEffect(() => {
    if (value) {
      const time = value.split(':');
      if (
        time.length !== 2 ||
        isNaN(parseInt(time[0])) ||
        isNaN(parseInt(time[1]))
      ) {
        return;
      }

      if (
        value !==
        `${times.hour?.toString().padStart(hourMaxLength, '0')}:${times.minute
          ?.toString()
          .padStart(2, '0')}`
      ) {
        setTimes({
          hour: parseInt(time[0]),
          minute: parseInt(time[1]),
        });
      }
    }
  }, [value]);
  
  React.useEffect(() => {
    if (times.hour === null || times.minute === null) {
      if (autoComplete && (times.hour || times.minute)) {
        return onChange(
          `${times.hour ? times.hour?.toString().padStart(hourMaxLength, '0') : '00'.padStart(hourMaxLength, '0')}:${
            times.minute ? times.minute?.toString().padStart(2, '0') : '00'
          }`,
        );
      }
      return onChange('');
    }

    if (
      value !==
      `${times.hour?.toString().padStart(hourMaxLength, '0')}:${times.minute
        ?.toString()
        .padStart(2, '0')}`
    ) {
      return onChange(
        `${times.hour.toString().padStart(hourMaxLength, '0')}:${times.minute
          .toString()
          .padStart(2, '0')}`,
      );
    }
  }, [times]);

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: '1fr 35px 1fr 35px',
        alignItems: 'center',
      }}
    >
      <Input
        className='min-w-0'
        disabled={disabled}
        type={'number'}
        min={hourOptions.min}
        max={hourOptions.max}
        step={hourOptions.step}
        value={times.hour?.toString().padStart(hourMaxLength, '0') || ''}
        onChange={e => {
          // 動態匹配數字位數
          const regx = new RegExp(`\\d{${hourMaxLength}}$|\\d$`);
          const _value = e.target.value.padStart(hourMaxLength, '0');
          setTimes(prev => {
            return ({
            ...prev,
            hour: e.target.value ? parseInt((_value.match(regx) || ['0'])[0]) : null,
          })})
        }}
        error={error}
        data-test-id={'hour'}
        data-testid={'hour'}
      />
      <span css={{textAlign: 'center'}}>時</span>
      <Input
        className='min-w-0'
        disabled={disabled}
        type={'number'}
        min={minuteOptions.min}
        max={minuteOptions.max}
        step={minuteOptions.step}
        value={times.minute?.toString().padStart(2, '0') || ''}
        onChange={e => {
          setTimes(prev => ({
            ...prev,
            minute: e.target.value ? parseInt((e.target.value.match(/\d{2}$|\d$/) || ['0'])[0]) : null,
          }))
        }}
        error={error}
        data-test-id={'minute'}
        data-testid={'minute'}
      />
      <span css={{textAlign: 'center'}}>分</span>
      <ErrorMessage css={{gridColumnStart: 1, gridColumnEnd: 5}}>
        {error}
      </ErrorMessage>
    </div>
  );
}

export function TimesInputNumber({
  value,
  onChange,
  hourOptions = {
    min: 0,
    max: 23,
    step: 1,
  },
  minuteOptions = {
    min: 0,
    max: 59,
    step: 1,
  },
  error,
  disabled = false,
  autoComplete = false,
}: TimeInput) {
  const hourMaxLength = hourOptions.max.toString().length;
  const [times, setTimes] = React.useState<{
    hour: number | null;
    minute: number | null;
  }>(() => {
    const nullState = {
      hour: null,
      minute: null,
    };
    if (isNilOrEmpty(value)) {
      return nullState;
    }

    const times = value.split(':');
    if (times.length < 2) {
      return nullState;
    }

    if (
      times.length !== 2 ||
      isNaN(parseInt(times[0])) ||
      isNaN(parseInt(times[1]))
    ) {
      return nullState;
    }

    return {
      hour: parseInt(times[0]),
      minute: parseInt(times[1]),
    };
  });
  React.useEffect(() => {
    if (value) {
      const time = value.split(':');
      if (
        time.length !== 2 ||
        isNaN(parseInt(time[0])) ||
        isNaN(parseInt(time[1]))
      ) {
        return;
      }

      if (
        value !==
        `${times.hour?.toString().padStart(hourMaxLength, '0')}:${times.minute
          ?.toString()
          .padStart(2, '0')}`
      ) {
        setTimes({
          hour: parseInt(time[0]),
          minute: parseInt(time[1]),
        });
      }
    }
  }, [value]);
  
  React.useEffect(() => {
    if (times.hour === null || times.minute === null) {
      if (autoComplete && (times.hour || times.minute)) {
        return onChange(
          `${times.hour ? times.hour?.toString().padStart(hourMaxLength, '0') : '00'.padStart(hourMaxLength, '0')}:${
            times.minute ? times.minute?.toString().padStart(2, '0') : '00'
          }`,
        );
      }
      return onChange('');
    }

    if (
      value !==
      `${times.hour?.toString().padStart(hourMaxLength, '0')}:${times.minute
        ?.toString()
        .padStart(2, '0')}`
    ) {
      return onChange(
        `${times.hour.toString().padStart(hourMaxLength, '0')}:${times.minute
          .toString()
          .padStart(2, '0')}`,
      );
    }
  }, [times]);

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: '1fr 33px 1fr 23px',
        alignItems: 'center',
      }}
    >
      <InputNumber
        type='text'
        min={hourOptions.min}
        max={hourOptions.max}
        step={hourOptions.step}
        value={times.hour || 0}
        onChange={(result: any) => {
          setTimes(prev => {
            return ({
            ...prev,
            hour: result ? parseInt(result) : null,
          })})
        }}
        isError={error ? true : false}
        data-test-id={'hour'}
        data-testid={'hour'}
      />
      <span css={{textAlign: 'center'}}>時</span>
      <InputNumber
        type='text'
        disabled={disabled}
        min={minuteOptions.min}
        max={minuteOptions.max}
        step={minuteOptions.step}
        value={times.minute || 0}
        onChange={(result: any) => {
          setTimes(prev => ({
            ...prev,
            minute: result ? parseInt(result) : null,
          }))
        }}
        isError={error ? true : false}
        data-test-id={'minute'}
        data-testid={'minute'}
      />
      <span className='pl-2.5'>分</span>
      <ErrorMessage css={{gridColumnStart: 1, gridColumnEnd: 5}}>
        {error}
      </ErrorMessage>
    </div>
  );
}

export const Switch = (props: any) => {
  return (
    <ReactSwitch
      {...props}
      uncheckedIcon={false}
      checkedIcon={false}
      activeBoxShadow={null}
      onColor="#26abe3"
      height={16}
      width={41}
    />
  );
};

interface DatePickerProps {
  error?: boolean;
}
export const DatePicker = styled(ReactDatePicker)<
  ReactDatePickerProps & DatePickerProps
>`
  width: 100%;
  height: 35px;
  color: #353d45;
  border-radius: 2px;
  border: 1px solid transparent;
  padding: 7px;
  background: ${props =>
    (props.disabled && hexToRgba('#F9F9F9', '.3')) || '#F9F9F9'} !important;
  cursor: ${props => (props.disabled && 'not-allowed') || 'text'};
  border-color: ${props =>
    (props.error && '#ff3b30') ||
    (props.disabled && hexToRgba('#d5dadd', '.3')) ||
    '#d5dadd'};
  &:hover {
    border-color: ${props =>
      (props.error && '#ff3b30') ||
      (props.disabled && hexToRgba('#d5dadd', '.3')) ||
      '#26ABE3'};
  }
  &:focus {
    outline: none;
    border-color: ${props =>
      (props.error && '#ff3b30') ||
      (props.disabled && hexToRgba('#d5dadd', '.3')) ||
      '#26ABE3'};
    box-shadow: ${props =>
      (props.error && `0 0 3px 0 #ff3b30`) ||
      (props.disabled && `none`) ||
      `0 0 3px 0 #26ABE3`};
  }
`;

export const FormItemButton = (props: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      css={{
        marginTop: 'calc(19px + .5rem)',
        display: 'flex',
        alignItems: 'center',
        height: 35,
      }}
      {...props}
    />
  );
};

type IconButtonProps = HTMLAttributes<HTMLButtonElement> & {children?: React.ReactNode; active?: boolean; disabled?: boolean; text?: string | React.ReactElement; type?: string }
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(({
  children,
  className,
  disabled,
  text,
  active,
  type = 'button',
  ...buttonProps
}, ref) => {
  return (
    <button
      ref={ref}
      className={`text-xl hover:text-brand ${className} disabled:opacity-25 flex items-center ${active ? 'text-brand' : ''}`}
      disabled={disabled}
      {...buttonProps}
    >
      {children}
      {text && <span className="text-[13px] pl-[3px]">{text}</span>}
    </button>
  );
});